import { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useFacilitiesByUserId,
  useGetCurrentLocalFacilityId,
} from "@/modules/facility/facilityHooks";
import { getUserIdFromToken } from "@/modules/user/userHooks";
import { Clubhouse_LeagueFacilityScope } from "../../../enums";
import type { Clubhouse_LeagueV2DetailFacilityDto } from "../../../types/leagueDetails";
import { ProgressiveRow, ProgressiveRowGroup } from "../ProgressiveRow";
import type { AdvancedLeagueFormValues } from "../advancedLeagueSchema";

const { THIS_FACILITY, ANY_OF_MY_FACILITIES } = Clubhouse_LeagueFacilityScope;

type LocationTabProps = {
  isEdit?: boolean;
  /**
   * The league's saved participating facilities from the detail (edit only).
   * The checklist can only seed from these when they carry a
   * `clubhouseFacilityId` — the one id space the operator's facilities share.
   * Until the backend returns it (CLUB-2357) the saved set is unresolvable, and
   * edit is gated read-only rather than guessed at.
   */
  savedFacilities?: Clubhouse_LeagueV2DetailFacilityDto[];
};

export function LocationTab({
  isEdit = false,
  savedFacilities,
}: LocationTabProps = {}) {
  const { control, setValue } = useFormContext<AdvancedLeagueFormValues>();
  const selected =
    useWatch({ control, name: "participatingClubhouseFacilityIds" }) ?? [];

  const [userFacilities] = useFacilitiesByUserId(getUserIdFromToken());
  const homeFacilityId = useGetCurrentLocalFacilityId();

  const facilities = useMemo(
    () => userFacilities.filter((f) => f.clubhouseFacilityId != null),
    [userFacilities],
  );
  const homeClubhouseId = useMemo(
    () => facilities.find((f) => f.id === homeFacilityId)?.clubhouseFacilityId,
    [facilities, homeFacilityId],
  );
  // Editing is only safe when the ENTIRE saved assignment resolves to facilities
  // the operator can see. A partial resolution (e.g. saved A+B+C, only A+B
  // visible) would let a save silently drop the unresolved facilities, so treat
  // it the same as fully-unresolved: gate read-only rather than seed a subset.
  const savedResolvedFully = useMemo(() => {
    const entries = savedFacilities ?? [];
    return (
      entries.length > 0 &&
      entries.every(
        (f) =>
          f.clubhouseFacilityId != null &&
          facilities.some(
            (x) => x.clubhouseFacilityId === f.clubhouseFacilityId,
          ),
      )
    );
  }, [savedFacilities, facilities]);

  const savedIds = useMemo(
    () =>
      savedResolvedFully
        ? (savedFacilities ?? []).map((f) => f.clubhouseFacilityId as number)
        : [],
    [savedResolvedFully, savedFacilities],
  );

  // On edit we must not present a guessed or partial selection as saved config:
  // gate the checklist read-only unless the whole saved set resolved.
  const gated = isEdit && !savedResolvedFully;

  const [open, setOpen] = useState(true);

  // Seed a default when nothing's chosen yet: the exact saved set on edit, the
  // home facility on create. Never guess an editable set on edit (see `gated`).
  // Not dirtied — a default the operator never chose shouldn't flag changes.
  useEffect(() => {
    if (gated || selected.length > 0 || facilities.length === 0) return;
    const seed =
      savedIds.length > 0
        ? savedIds
        : homeClubhouseId != null
          ? [homeClubhouseId]
          : [facilities[0]!.clubhouseFacilityId as number];
    setValue("participatingClubhouseFacilityIds", seed, { shouldDirty: false });
    setValue(
      "facilityScope",
      seed.length > 1 ? ANY_OF_MY_FACILITIES : THIS_FACILITY,
      { shouldDirty: false },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gated, selected.length, facilities, homeClubhouseId, savedIds]);

  const toggle = (id: number) => {
    const isOn = selected.includes(id);
    if (isOn && selected.length === 1) return; // can't uncheck the last
    const next = isOn ? selected.filter((x) => x !== id) : [...selected, id];
    setValue("participatingClubhouseFacilityIds", next, { shouldDirty: true });
    setValue(
      "facilityScope",
      next.length > 1 ? ANY_OF_MY_FACILITIES : THIS_FACILITY,
      { shouldDirty: true },
    );
  };

  if (gated) {
    return (
      <ProgressiveRowGroup>
        <ProgressiveRow
          label="Facilities"
          locked
          value="Locked"
          open={open}
          onOpenChange={setOpen}
        >
          <div className="flex flex-col gap-3">
            <p className="text-sm text-text-muted">
              Facility assignments can&apos;t be edited yet because this
              league&apos;s saved facility list isn&apos;t fully available. This
              unlocks automatically once it is.
            </p>
            {savedFacilities && savedFacilities.length > 0 && (
              <div className="flex flex-col gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Currently played at
                </p>
                <ul className="flex flex-col gap-2">
                  {savedFacilities.map((f, i) => (
                    <li
                      key={f.id ?? i}
                      className="rounded-xl border border-border bg-muted/40 px-3 py-3"
                    >
                      <span className="poppins text-sm font-semibold tracking-tight text-text-primary">
                        {f.name}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </ProgressiveRow>
      </ProgressiveRowGroup>
    );
  }

  return (
    <ProgressiveRowGroup>
      <ProgressiveRow
        label="Facilities"
        settled={selected.length > 0}
        value={`${selected.length} of ${facilities.length} selected`}
        open={open}
        onOpenChange={setOpen}
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-text-muted">
            Check or uncheck a facility to change where this league can be
            played.
          </p>
          <ul className="flex flex-col gap-2">
            {facilities.map((f) => {
              const id = f.clubhouseFacilityId as number;
              const on = selected.includes(id);
              const isLast = on && selected.length === 1;
              return (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => toggle(id)}
                    disabled={isLast}
                    aria-pressed={on}
                    aria-label={f.name}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
                      on
                        ? "border-primary/40 bg-primary/[0.04]"
                        : "border-border bg-surface hover:border-primary/20 hover:bg-muted/40",
                      isLast
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer",
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "flex size-5 shrink-0 items-center justify-center rounded-sm border-2 transition-colors",
                        on
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-muted-foreground/60 bg-surface",
                      )}
                    >
                      {on && <Check className="size-3.5 stroke-[3]" />}
                    </span>
                    <span className="poppins text-sm font-semibold tracking-tight text-text-primary">
                      {f.name}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="text-sm text-text-muted">
            This only controls where the league can be played — Schedule and
            Roster stay league-wide. At least one facility must stay selected.
          </p>
        </div>
      </ProgressiveRow>
    </ProgressiveRowGroup>
  );
}
